/* eslint-disable jsx-a11y/label-has-associated-control */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Loader, ModalGeneric, Switch, TextArea } from 'ypf-pas-general-ui-components';

import { capitalize, formatDateToSpanish, validateZerosInput } from '../../../utils/formatUtils';
import { cancelQuantity } from '../../../services/workReport';
import useToast from '../../../hooks/useToast';
import '../WorkReportModals.scss';
import './EditQuantityModal.scss';
import { checkRol } from '../../../security';
import { inspector, aprobador } from '../../../security/constantsRols';
import LoaderContext from '../../../context/LoaderProvider';
import ProgramPointList from '../ProgramPointModal/ProgramPointList/ProgramPointList';
import { ubicationTypeEnum } from '../../../utils/dataStatus';
import useLocationProgramPoints, {
  defaultInitialValues
} from '../ProgramPointModal/useLocationProgramPoints';

// solo sirve para mostrar el alert
const hasInvalidProgramPoint = (programPoint) =>
  programPoint?.code === null ||
  programPoint?.code === '' ||
  programPoint?.subCode === null ||
  programPoint?.subCode === '' ||
  programPoint?.cost === null ||
  programPoint?.cost === '' ||
  programPoint?.cost?.nombre === '' ||
  programPoint?.code?.nombre === '' ||
  programPoint?.subCode?.nombre === '';

const handleCancel = async (dailyWorkReportId, comment) =>
  cancelQuantity({
    idParteDiario: dailyWorkReportId,
    comentario: comment
  });

const applyClassnameInspector = (isInspector) => (isInspector ? 'cancel-quantity-label ' : '');

const EditQuantityModal = (props) => {
  const {
    show,
    onHide,
    onAccept,
    dailyWorkReportId,
    positionName,
    lineName,
    um,
    quantity,
    date,
    yClickCertification,
    estadoEnum,
    tienePuntosPrograma,
    tipoUbicacionEnum,
    tipoAsignacion,
    eventoSigla,
    line
  } = props;

  const [disableButton, setDisableButton] = useState(false);
  const [disablePrimaryAction, setDisablePrimaryAction] = useState(false);
  const { day, month, year } = formatDateToSpanish(date);
  const { setNewToast } = useToast();
  const { setLoader } = useContext(LoaderContext);
  const [isCancel, setIsCancel] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const isInspector = checkRol(inspector);
  const isApprover = checkRol(aprobador);

  const isPozoOrLocation =
    tipoUbicacionEnum === ubicationTypeEnum.pozo ||
    tipoUbicacionEnum === ubicationTypeEnum.locacion;

  const dontShowTextArea = isApprover && !yClickCertification;

  const defaultValues = {
    ...defaultInitialValues,
    quantity,
    comment: ''
  };

  const {
    getProgramPointsData,
    calculateTotalValue,
    editQuantity,
    editWithProgramPoints,
    values,
    errors,
    setFieldValue,
    resetForm,
    touched,
    loading
  } = useLocationProgramPoints({
    line,
    initialValues: defaultValues,
    validationSchema: undefined
  });

  useEffect(() => {
    if (show) {
      setFieldValue('quantity', quantity);
      if (isPozoOrLocation) {
        getProgramPointsData();
      }
    } else resetForm();
  }, [show]);

  useEffect(() => {
    const hasCompleteData = values.locations.every((location) =>
      location.programPoints.every(
        (PP) =>
          Object.prototype.hasOwnProperty.call(PP, 'code') &&
          Object.prototype.hasOwnProperty.call(PP, 'subCode') &&
          Object.prototype.hasOwnProperty.call(PP, 'cost') &&
          Object.prototype.hasOwnProperty.call(PP, 'clasification')
      )
    );

    setDisablePrimaryAction(!isPozoOrLocation && !values.quantity);
    if (hasCompleteData) {
      setShowAlert(
        values.locations.some((location) => location.programPoints.some(hasInvalidProgramPoint))
      );
    } else {
      setShowAlert(true);
    }
  }, [isPozoOrLocation, values.locations, values.programPoints, values.quantity]);

  useEffect(() => {
    if (!!Object.values(errors).length || !!Object.values(touched).length) {
      setDisableButton(true);
    } else {
      setDisableButton(false);
    }
  }, [errors, touched]);

  const onExit = () => {
    resetForm();
    onHide();
  };

  const handleCommunicate = async () => {
    try {
      setLoader(true);
      let response;

      if (isCancel) {
        response = await handleCancel(dailyWorkReportId, values?.comment);
        setNewToast({
          type: 'SUCCESS',
          message: response?.mensaje
        });
      } else if (isPozoOrLocation) {
        // uses perfilEjecuta internally to differentiate between approver and inspector
        response = await editWithProgramPoints({
          cantidad: values?.locationTotalValue,
          comentario: values?.comment,
          locations: values?.locations
        });
      } else {
        response = await editQuantity({
          cantidad: Number(values?.quantity),
          comentario: values?.comment
        });
      }

      if (response) {
        onAccept({
          isCancel,
          newQuantity: isPozoOrLocation ? values?.locationTotalValue : Number(values.quantity)
        });
      }
      onExit();
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    } finally {
      setLoader(false);
    }
  };

  const hasTotalValuesChange = JSON.stringify(
    values.locations.map((location) => location.totalValue)
  );

  useEffect(() => {
    const { locationTotalValue, locationTotalValueFijado } = calculateTotalValue(values.locations);

    setFieldValue(`locationTotalValue`, locationTotalValue);
    setFieldValue(`locationTotalValueFijado`, locationTotalValueFijado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTotalValuesChange]);

  const getInvalidQuantity = () =>
    isPozoOrLocation &&
    (values?.locations || [])
      .filter((loc) => loc?.habilitaVentana)
      .flatMap((location) => location?.programPoints || [])
      .find((qntyObj) => !qntyObj?.quantity);

  const invalidQuantity = getInvalidQuantity();

  const inspectorPrimaryActionDisabled = () =>
    (isInspector && !isPozoOrLocation && !values?.quantity && !isCancel) ||
    (isInspector && !isPozoOrLocation && Number(values.quantity) === 0 && !isCancel) ||
    (isInspector && isPozoOrLocation && Number(values?.locationTotalValue) === 0 && !isCancel) ||
    (isInspector && !isPozoOrLocation && !isCancel && Number(values.quantity) === quantity);

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={onExit}
      primaryAction={() => {
        handleCommunicate();
      }}
      primaryActionDisabled={
        loading ||
        disableButton ||
        invalidQuantity ||
        (isApprover && values.isPP && disablePrimaryAction) ||
        values?.comment === '' ||
        inspectorPrimaryActionDisabled() ||
        (isPozoOrLocation && Number(values?.locationTotalValue) === values.quantity && !isCancel)
      }
      primaryActionLabel="Comunicar"
      secondaryAction={onExit}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Editar"
    >
      <>
        {loading && <Loader show={loading} text="CARGANDO ..." />}
        {isPozoOrLocation ? (
          <>
            <ProgramPointList
              disabledInputPP={(checkRol(aprobador) && !isPozoOrLocation) || isInspector}
              disabledInputQuantity={false}
              displayButtons={!isInspector && isPozoOrLocation}
              editCSA={!isInspector}
              eventoSigla={eventoSigla}
              idParteDiario={dailyWorkReportId}
              isLocation
              line={{
                lineName,
                positionName,
                estadoEnum,
                tienePuntosPrograma,
                unidadMedida: um
              }}
              loading={loading}
              selectedDate={date}
              setFieldValue={setFieldValue}
              showAlert={showAlert}
              showRectifyQuantityButton={false}
              showTitle
              showWarningOperationalData
              tipoAsignacion={tipoAsignacion}
              values={values}
              viewCSA
            />

            <div className="label-switch-textarea">
              {isInspector && (
                <label className="cancel-quantity-label">
                  Rechazar cantidad
                  <Switch
                    checked={isCancel}
                    falseLabel="NO"
                    name="cancelQuantity"
                    onChange={({ target }) => setIsCancel(!target.checked)}
                    size="sm"
                    trueLabel="SÍ"
                  />
                </label>
              )}
              <TextArea
                className={`required-field textarea ${applyClassnameInspector(isInspector)}`}
                errors="invalido"
                isInvalid={false}
                label="Comentario"
                maxLength={512}
                name="comments"
                onChange={(e) => {
                  setFieldValue('comment', e.target.value);
                }}
                placeholder="Motivo de la modificación"
                resize={false}
                rows={1}
                value={values?.comment}
              />
            </div>
          </>
        ) : (
          <>
            <div className="text-center modal-info">
              <div className="d-flex justify-content-center flex-wrap">
                <span>&quot;{positionName}</span>
                <span>{lineName}</span>
                <span>
                  {um} {values?.quantity.toLocaleString('es-AR')}&quot;
                </span>
              </div>
              Para el día {capitalize(day?.text)}, {day?.number} de {month} de {year}
            </div>
            <hr className="my-4" />
            <div className="d-flex align-items-end gap-4 mb-4">
              {!isPozoOrLocation && (
                <Input
                  className={isInspector || !dontShowTextArea ? '' : 'edit-quantity-input'}
                  disabled={isCancel}
                  label="Proponer nueva cantidad"
                  name="quantity"
                  onChange={(e) => {
                    const { value } = e.target;

                    if (validateZerosInput(value, 3)) {
                      setFieldValue('quantity', value);
                    }
                  }}
                  type="number"
                  value={values?.quantity}
                />
              )}
              {isInspector && (
                <label className="cancel-quantity-label">
                  Rechazar cantidad
                  <Switch
                    checked={isCancel}
                    falseLabel="NO"
                    name="cancelQuantity"
                    onChange={({ target }) => setIsCancel(!target.checked)}
                    size="sm"
                    trueLabel="SÍ"
                  />
                </label>
              )}
            </div>
            <TextArea
              className={`required-field isInspector ${isInspector ? 'cancel-quantity-label' : ''}`}
              errors="invalido"
              isInvalid={false}
              label="Comentario"
              maxLength={512}
              name="comments"
              onChange={(e) => {
                setFieldValue('comment', e.target.value);
              }}
              placeholder="Motivo de la modificación"
              resize={false}
              rows={4}
              value={values?.comment}
            />
          </>
        )}
      </>
    </ModalGeneric>
  );
};

export default EditQuantityModal;

EditQuantityModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onAccept: PropTypes.func,
  dailyWorkReportId: PropTypes.number,
  positionName: PropTypes.string,
  lineName: PropTypes.string,
  um: PropTypes.string,
  quantity: PropTypes.number,
  date: PropTypes.string,
  yClickCertification: PropTypes.bool,
  estadoEnum: PropTypes.number.isRequired,
  tienePuntosPrograma: PropTypes.bool.isRequired,
  tipoUbicacionEnum: PropTypes.number.isRequired,
  eventoSigla: PropTypes.string.isRequired,
  tipoAsignacion: PropTypes.number.isRequired,
  line: PropTypes.shape({})
};

EditQuantityModal.defaultProps = {
  show: false,
  onHide: () => {
    /* Empty */
  },
  onAccept: () => {
    /* Empty */
  },
  dailyWorkReportId: null,
  positionName: '',
  lineName: '',
  um: '',
  quantity: 0,
  date: '',
  yClickCertification: false,
  line: {}
};
